import "@/App.css";
import { useEffect, useRef, useState } from "react";
import { useTheme } from "next-themes";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";

// Logo Cockpit SVG Component
const CockpitLogo = ({ className = "h-12" }) => (
  <img
    src="/logo.png"
    alt="Cockpit Logo"
    className={className}
  />
);

// Navbar Component
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const scrollToContact = () => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? 'bg-navy-900/95 backdrop-blur-md border-b border-slate-800/60' : ''}`} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a href="#" className="flex items-center gap-2 py-2" data-testid="logo-link">
            <CockpitLogo className="h-14 w-auto object-contain" />
          </a>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#problem" className="text-sm text-slate-400 hover:text-white transition-colors" data-testid="nav-problem">Problème</a>
            <a href="#product" className="text-sm text-slate-400 hover:text-white transition-colors" data-testid="nav-product">Produit</a>
            <a href="#features" className="text-sm text-slate-400 hover:text-white transition-colors" data-testid="nav-features">Fonctionnalités</a>
            <a href="#agent" className="text-sm text-slate-400 hover:text-white transition-colors" data-testid="nav-agent">Agent</a>
            {/* <a href="#pricing" className="text-sm text-slate-400 hover:text-white transition-colors" data-testid="nav-pricing">Tarifs</a> */}
            <a href="#faq" className="text-sm text-slate-400 hover:text-white transition-colors" data-testid="nav-faq">FAQ</a>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button onClick={scrollToContact} className="btn-primary text-sm text-white font-semibold px-4 py-2 rounded-lg" data-testid="nav-cta">
              Demander une démo
            </button>
            <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors" aria-label="Basculer le thème">
              {theme === 'dark' ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
          </div>

          <button 
            className="md:hidden p-2 text-slate-400 hover:text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-navy-900/98 backdrop-blur-md border-b border-slate-800/60">
          <div className="px-4 py-4 space-y-3">
            <a href="#problem" className="block text-sm text-slate-400 hover:text-white">Problème</a>
            <a href="#product" className="block text-sm text-slate-400 hover:text-white">Produit</a>
            <a href="#features" className="block text-sm text-slate-400 hover:text-white">Fonctionnalités</a>
            <a href="#agent" className="block text-sm text-slate-400 hover:text-white">Agent</a>
            {/* <a href="#pricing" className="block text-sm text-slate-400 hover:text-white">Tarifs</a> */}
            <a href="#faq" className="block text-sm text-slate-400 hover:text-white">FAQ</a>
            <div className="pt-3 border-t border-slate-800">
              <div className="flex gap-2">
                <button onClick={() => { setMobileMenuOpen(false); scrollToContact(); }} className="btn-primary flex-1 text-center text-sm text-white font-semibold px-4 py-2 rounded-lg">
                  Demander une démo
                </button>
                <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className="p-2 rounded-lg border border-slate-700 text-slate-400" aria-label="Basculer le thème">
                  {theme === 'dark' ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => (
  <section className="relative min-h-screen flex items-center pt-20 overflow-hidden" data-testid="hero-section">
    {/* Background layers */}
    <div className="absolute inset-0 bg-navy-900 hero-grid" aria-hidden="true"></div>
    <div className="orb w-[520px] h-[520px] bg-blue-600 opacity-[.11]" style={{top:'-8%', left:'-6%'}} aria-hidden="true"></div>
    <div className="orb w-[420px] h-[420px] bg-emerald-500 opacity-[.09]" style={{top:'55%', right:'-6%'}} aria-hidden="true"></div>
    <div className="orb w-[300px] h-[300px] bg-blue-400 opacity-[.07]" style={{bottom:'5%', left:'38%'}} aria-hidden="true"></div>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full pt-8 pb-20 lg:pt-10 lg:pb-28">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        {/* Left column */}
        <div>
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full glass text-xs font-medium text-slate-300 mb-7">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" aria-hidden="true"></span>
            Sage 100 · Bientôt Sage X3
          </div>

          <p className="text-sm font-semibold text-blue-400 uppercase tracking-widest mb-3">
            Tableau de bord financier pour PME
          </p>
          <h1 className="text-[2.6rem] sm:text-5xl lg:text-[3.2rem] font-extrabold leading-[1.12] text-white mb-4 tracking-tight" data-testid="hero-title">
            Pilotez votre entreprise<br/>
            avec vos <span className="grad">vrais chiffres</span>,<br/>
            en temps réel.
          </h1>
          <p className="text-[15px] text-slate-400 mb-7 leading-relaxed max-w-lg">
            Cockpit se connecte à votre logiciel Sage et transforme vos données comptables en tableaux de bord clairs — sans export Excel, sans IT.
          </p>

          <ul className="space-y-3 mb-9" aria-label="Points clés">
            <li className="flex items-start gap-3 text-[15px] text-slate-300 leading-snug">
              <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"></path></svg>
              <span><strong className="text-white">Posez vos questions en français</strong> — chiffre d'affaires, trésorerie, marges, délais de paiement : <strong className="text-white">Zuri</strong>, notre IA, répond instantanément.</span>
            </li>
            <li className="flex items-start gap-3 text-[15px] text-slate-300 leading-snug">
              <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"></path></svg>
              <span><strong className="text-white">Vos données ne sortent jamais de vos locaux</strong> — l'agent interroge votre Sage sur place.</span>
            </li>
            <li className="flex items-start gap-3 text-[15px] text-slate-300 leading-snug">
              <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"></path></svg>
              <span><strong className="text-white">Vos analyses restent accessibles</strong> — sans ressaisie, sans export Excel.</span>
            </li>
          </ul>

          <div className="flex flex-col sm:flex-row gap-4 mb-11">
            <button onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })} className="btn-primary inline-flex items-center justify-center gap-2 text-white font-bold px-7 py-3.5 rounded-xl text-[15px]" data-testid="hero-cta-primary">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
              Demander une démo
            </button>
            <a href="#product" className="btn-ghost inline-flex items-center justify-center gap-2 text-slate-300 font-medium px-7 py-3.5 rounded-xl text-[15px]" data-testid="hero-cta-secondary">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"></path><path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
              Voir le produit
            </a>
          </div>

        </div>

        {/* Right column - Dashboard mockup */}
        <div className="relative hidden lg:block" aria-hidden="true">
          <div className="animate-float">
            <DashboardMockup />
          </div>

          {/* Floating badges */}
          <div className="absolute -left-12 top-[28%] glass rounded-xl px-4 py-3 border border-emerald-500/25 animate-float-slow shadow-xl" style={{animationDelay:'2s'}}>
            <p className="text-[10px] text-slate-500 mb-0.5">Trésorerie nette</p>
            <p className="text-base font-bold text-emerald-400">+124M FCFA</p>
          </div>

          <div className="absolute -right-10 bottom-[22%] glass rounded-xl px-4 py-3 border border-blue-500/25 animate-float shadow-xl" style={{animationDelay:'4s'}}>
            <p className="text-[10px] text-slate-500 mb-0.5">Agent Sage 100</p>
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              <p className="text-sm font-semibold text-white">Online · 30s</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Bottom wave */}
    <div className="absolute bottom-0 inset-x-0 pointer-events-none" aria-hidden="true">
      <svg viewBox="0 0 1440 56" fill="none" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-14">
        <path d="M0 56L1440 56L1440 28C1200 56 960 0 720 20C480 40 240 4 0 28L0 56Z" fill="var(--navy-800)"></path>
      </svg>
    </div>
  </section>
);

// Dashboard Mockup Component
const DashboardMockup = () => (
  <div className="glass rounded-2xl overflow-hidden shadow-2xl" style={{boxShadow: '0 0 70px rgba(59,130,246,.18)'}}>
    {/* Window chrome */}
    <div className="flex items-center justify-between px-4 py-3 bg-slate-100 dark:bg-slate-900/70 border-b border-slate-200 dark:border-slate-700/50">
      <div className="flex gap-1.5">
        <div className="w-3 h-3 rounded-full bg-red-400/80"></div>
        <div className="w-3 h-3 rounded-full bg-yellow-400/80"></div>
        <div className="w-3 h-3 rounded-full bg-green-400/80"></div>
      </div>
      <div className="flex items-center gap-2 px-3 py-1 rounded-md bg-white dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700/40">
        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
        <span className="text-[11px] text-slate-500 dark:text-slate-400 font-mono">Cockpit — Votre ERP devient un cockpit financier</span>
      </div>
      <div className="w-16"></div>
    </div>

    <div className="p-5 bg-slate-50/60 dark:bg-transparent">
      {/* KPI row */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-white dark:bg-slate-800/60 rounded-xl p-3 border border-slate-200 dark:border-slate-700/40 shadow-sm dark:shadow-none">
          <p className="text-[10px] text-slate-500 dark:text-slate-500 mb-1 uppercase tracking-wide">CA Mois</p>
          <p className="text-[17px] font-bold text-slate-800 dark:text-white leading-none">847M <span className="text-[10px] text-slate-400 dark:text-slate-500 font-normal">FCFA</span></p>
          <p className="text-[11px] text-emerald-500 dark:text-emerald-400 mt-1 flex items-center gap-0.5">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path></svg>
            +12.4% vs N-1
          </p>
        </div>
        <div className="bg-white dark:bg-slate-800/60 rounded-xl p-3 border border-slate-200 dark:border-slate-700/40 shadow-sm dark:shadow-none">
          <p className="text-[10px] text-slate-500 dark:text-slate-500 mb-1 uppercase tracking-wide">Marge brute</p>
          <p className="text-[17px] font-bold text-slate-800 dark:text-white leading-none">38.2<span className="text-[10px] text-slate-400 dark:text-slate-500 font-normal">%</span></p>
          <p className="text-[11px] text-emerald-500 dark:text-emerald-400 mt-1 flex items-center gap-0.5">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path></svg>
            +2.1 pts
          </p>
        </div>
        <div className="bg-white dark:bg-slate-800/60 rounded-xl p-3 border border-slate-200 dark:border-slate-700/40 shadow-sm dark:shadow-none">
          <p className="text-[10px] text-slate-500 dark:text-slate-500 mb-1 uppercase tracking-wide">DSO (DMP)</p>
          <p className="text-[17px] font-bold text-slate-800 dark:text-white leading-none">42 <span className="text-[10px] text-slate-400 dark:text-slate-500 font-normal">jours</span></p>
          <p className="text-[11px] text-amber-500 dark:text-amber-400 mt-1 flex items-center gap-0.5">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"></path></svg>
            -3j ce mois
          </p>
        </div>
      </div>

      {/* Mini chart */}
      <div className="bg-white dark:bg-slate-800/50 rounded-xl p-4 mb-4 border border-slate-200 dark:border-slate-700/30 shadow-sm dark:shadow-none">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[11px] text-slate-500 dark:text-slate-400">Évolution CA — 8 derniers mois</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-500 dark:text-blue-400 border border-blue-500/25">Sage 100</span>
        </div>
        <div className="flex items-end gap-1.5 h-14">
          <div className="flex-1 rounded-sm bg-blue-300/60 dark:bg-blue-500/25" style={{height:'54%'}}></div>
          <div className="flex-1 rounded-sm bg-blue-300/60 dark:bg-blue-500/25" style={{height:'68%'}}></div>
          <div className="flex-1 rounded-sm bg-blue-300/60 dark:bg-blue-500/25" style={{height:'44%'}}></div>
          <div className="flex-1 rounded-sm bg-blue-400/60 dark:bg-blue-500/35" style={{height:'76%'}}></div>
          <div className="flex-1 rounded-sm bg-blue-400/60 dark:bg-blue-500/35" style={{height:'63%'}}></div>
          <div className="flex-1 rounded-sm bg-blue-500/70 dark:bg-blue-500/45" style={{height:'88%'}}></div>
          <div className="flex-1 rounded-sm bg-blue-600 dark:bg-blue-500" style={{height:'100%'}}></div>
          <div className="flex-1 rounded-sm bg-emerald-400/60 dark:bg-emerald-500/50" style={{height:'72%'}}></div>
        </div>
      </div>

      {/* NLQ input */}
      <div className="bg-white dark:bg-slate-900/70 rounded-xl px-4 py-3 border border-blue-200 dark:border-blue-500/25 flex items-center gap-3 shadow-sm dark:shadow-none">
        <svg className="w-4 h-4 text-blue-500 dark:text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path></svg>
        <span className="text-[13px] text-slate-500 dark:text-slate-400 font-mono cursor">"Clients avec retard &gt; 60j ce mois"</span>
        <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25 whitespace-nowrap">IA &lt;2s</span>
      </div>
    </div>
  </div>
);

// Trusted By Section
const TrustedBySection = () => (
  <section className="bg-navy-800 py-10 border-y border-slate-200 dark:border-slate-800/60" data-testid="trusted-by-section">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <p className="text-center text-[11px] text-slate-500 dark:text-slate-600 uppercase tracking-widest font-semibold mb-7">Conçu pour les entreprises qui utilisent</p>
      <div className="flex flex-wrap justify-center items-center gap-8 md:gap-14">
        <TrustBadge icon="sage" name="Sage 100" color="green" />
        <TrustBadge icon="sage" name="Sage X3" color="blue" soon />
      </div>
    </div>
  </section>
);

const TrustBadge = ({ icon, name, color, soon }) => {
  const colorClasses = {
    green: 'bg-green-100 dark:bg-green-700/30 border-green-400 dark:border-green-600/30 text-green-700 dark:text-green-500',
    blue: 'bg-blue-100 dark:bg-blue-700/20 border-blue-400 dark:border-blue-500/20 text-blue-700 dark:text-blue-400',
  };

  return (
    <div className="flex items-center gap-2.5 cursor-default">
      <div className={`w-7 h-7 rounded-md border flex items-center justify-center ${colorClasses[color]}`}>
        {icon === 'sage' && <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7 21l4-4 4 4M12 17V3M5 12l7 7 7-7"></path></svg>}
      </div>
      <span className="text-sm font-semibold text-slate-900 dark:text-slate-300">{name}</span>
      {soon && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-500 border border-slate-300 dark:border-slate-600">bientôt</span>}
    </div>
  );
};

// Problem Section
const ProblemSection = () => (
  <section id="problem" className="bg-navy-800 py-24" data-testid="problem-section">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16 reveal">
        <span className="text-xs text-red-400 uppercase tracking-widest font-semibold">Le problème</span>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-3 mb-4 leading-tight">
          Les équipes financières Sage perdent<br className="hidden sm:block"/>
          <span className="grad-orange">des jours entiers sur des tâches manuelles.</span>
        </h2>
        <p className="text-slate-400 text-lg max-w-2xl mx-auto">
          Vous avez Sage. Vous avez les données. Mais les obtenir sous forme de décision prend encore trop longtemps.
        </p>
      </div>

      {/* Pain points */}
      <div className="grid md:grid-cols-3 gap-5 mb-16">
        <PainCard 
          icon="excel"
          title="Excel en bout de chaîne"
          description="Chaque reporting commence par un export Sage, puis un copier-coller dans Excel. Le DAF passe 2 à 3 heures chaque lundi à consolider des données qui existaient déjà dans le système."
          impact="Impact estimé : 2–3h perdues / semaine / DAF"
          color="red"
        />
        <PainCard 
          icon="clock"
          title="Dépendance à la DSI pour chaque requête"
          description="Besoin d'un nouveau KPI ? Ticket à la DSI. Délai 3 à 10 jours. Pendant ce temps, la décision attend. Les managers financiers n'ont aucune autonomie sur leurs propres données."
          impact="Impact estimé : 3–10 jours de délai par requête"
          color="amber"
        />
        <PainCard 
          icon="lock"
          title="Risque sécurité des outils BI classiques"
          description="Les solutions BI traditionnelles copient vos données comptables dans le cloud. Pour des PME africaines avec des données financières sensibles, ce modèle est inacceptable."
          impact="Risque : données ERP exposées hors infrastructure"
          color="purple"
        />
      </div>

      {/* Solution transition */}
      <div className="relative reveal">
        <div className="glass-card rounded-2xl p-8 md:p-10 border border-blue-200 dark:border-blue-500/20 shadow-xl dark:shadow-none text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 dark:bg-blue-500/10 border border-blue-300 dark:border-blue-500/25 text-xs text-blue-700 dark:text-blue-400 font-semibold mb-5">
            La solution Cockpit
          </div>
          <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white leading-snug max-w-2xl mx-auto">
            Branchez Cockpit sur Sage.<br/>
            <span className="grad">Vos KPIs s'affichent en temps réel.</span><br/>
            Vos données ne bougent pas.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-8 mt-8">
            <SolutionBullet text="Autonomie totale — 0 ticket DSI" />
            <SolutionBullet text="Réponse en <30 secondes" />
            <SolutionBullet text="Vos données restent chez vous" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

const PainCard = ({ icon, title, description, impact, color }) => {
  const colorClasses = {
    red: { bg: 'bg-red-50 dark:bg-red-500/10', border: 'border-red-200 dark:border-red-500/20', text: 'text-red-600 dark:text-red-400' },
    amber: { bg: 'bg-amber-50 dark:bg-amber-500/10', border: 'border-amber-200 dark:border-amber-500/20', text: 'text-amber-700 dark:text-amber-400' },
    purple: { bg: 'bg-purple-50 dark:bg-purple-500/10', border: 'border-purple-200 dark:border-purple-500/20', text: 'text-purple-600 dark:text-purple-400' }
  };

  return (
    <div className="glass-card rounded-2xl p-6 reveal shadow-sm dark:shadow-none border border-slate-100 dark:border-transparent">
      <div className={`w-11 h-11 rounded-xl ${colorClasses[color].bg} ${colorClasses[color].border} border flex items-center justify-center mb-5`}>
        {icon === 'excel' && <svg className={`w-5 h-5 ${colorClasses[color].text}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7"></path></svg>}
        {icon === 'clock' && <svg className={`w-5 h-5 ${colorClasses[color].text}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>}
        {icon === 'lock' && <svg className={`w-5 h-5 ${colorClasses[color].text}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>}
      </div>
      <h3 className="text-base font-bold text-slate-900 dark:text-white mb-2">{title}</h3>
      <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{description}</p>
      <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700/50">
        <p className={`text-xs ${colorClasses[color].text} font-medium`}>{impact}</p>
      </div>
    </div>
  );
};

const SolutionBullet = ({ text }) => (
  <div className="flex items-center gap-3">
    <div className="w-9 h-9 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
      <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"></path></svg>
    </div>
    <span className="text-sm text-slate-300">{text}</span>
  </div>
);

// Product Preview Section
const ProductPreviewSection = () => {
  const [activeTab, setActiveTab] = useState('cockpit');

  return (
    <section id="product" className="bg-navy-900 py-24 overflow-hidden" data-testid="product-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 reveal">
          <span className="text-xs text-blue-400 uppercase tracking-widest font-semibold">Inside Cockpit</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-3 mb-4">
            Voyez exactement ce que voient<br className="hidden sm:block"/>vos DAFs au quotidien.
          </h2>
          <p className="text-slate-400 text-lg max-w-xl mx-auto">
            Trois vues. Toutes connectées à votre Sage en temps réel.
          </p>
        </div>

        {/* Tab switcher */}
        <div className="flex justify-center mb-8 reveal">
          <div className="glass inline-flex rounded-xl p-1 gap-1">
            <button
              className={`product-tab px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${activeTab === 'cockpit' ? 'active-tab' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}
              onClick={() => setActiveTab('cockpit')}
              data-testid="tab-cockpit"
            >
              Cockpit DAF
            </button>
            <button
              className={`product-tab px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${activeTab === 'nlq' ? 'active-tab' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}
              onClick={() => setActiveTab('nlq')}
              data-testid="tab-nlq"
            >
              Demandez à Zuri
            </button>
            <button
              className={`product-tab px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${activeTab === 'kpi' ? 'active-tab' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}
              onClick={() => setActiveTab('kpi')}
              data-testid="tab-kpi"
            >
              KPI Store
            </button>
          </div>
        </div>

        {/* Product window */}
        <div className="reveal" style={{filter: 'drop-shadow(0 0 60px rgba(59,130,246,0.12))'}}>
          <div className="glass rounded-2xl overflow-hidden border border-blue-200 dark:border-blue-500/15">
            {/* Window chrome */}
            <div className="flex items-center justify-between px-5 py-3 bg-slate-100 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-700/50">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-400/70"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-400/70"></div>
                <div className="w-3 h-3 rounded-full bg-green-400/70"></div>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-white dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700/40">
                <svg className="w-3 h-3 text-emerald-500 dark:text-emerald-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"></path></svg>
                <span className="text-[11px] text-slate-500 dark:text-slate-400 font-mono">
                  {activeTab === 'cockpit' && 'Cockpit — Votre ERP devient un cockpit financier'}
                  {activeTab === 'nlq' && 'Zuri — Posez vos questions, notre IA répond instantanément.'}
                  {activeTab === 'kpi' && "KPI Store — Vos KPIs préconfigurés et prêts à l'emploi."}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                <span className="text-[11px] text-slate-500 dark:text-slate-500">Agent Sage 100 · Online</span>
              </div>
            </div>

            {/* Tab content */}
            {activeTab === 'cockpit' && <CockpitDAFView />}
            {activeTab === 'nlq' && <NLQView />}
            {activeTab === 'kpi' && <KPIStoreView />}
          </div>
        </div>
      </div>
    </section>
  );
};

const CockpitDAFView = () => (
  <div className="p-5 md:p-6">
    {/* Top bar */}
    <div className="flex items-center justify-between mb-5">
      <div>
        <h3 className="text-sm font-bold text-slate-900 dark:text-white">Mon Cockpit — Mars 2026</h3>
        <p className="text-xs text-slate-500 mt-0.5">Dernière sync : il y a 28s · Sage 100 · Société principale</p>
      </div>
      <div className="flex items-center gap-2">
        <button className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
          Exporter PDF
        </button>
        <button className="text-xs px-3 py-1.5 rounded-lg bg-blue-500/15 border border-blue-500/30 text-blue-600 dark:text-blue-400 hover:bg-blue-500/25 transition-colors">
          + Ajouter widget
        </button>
      </div>
    </div>

    {/* KPI cards */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
      <KPICard label="Chiffre d'affaires" value="847M" unit="FCFA · ce mois" change="+12.4% vs N-1" positive />
      <KPICard label="Marge brute" value="38.2%" unit="Objectif : 40%" progress={95} />
      <KPICard label="DSO (DMP clients)" value="42j" unit="Seuil alerte : 60j" change="-3j ce mois" />
      <KPICard label="Trésorerie nette" value="+124M" unit="FCFA disponibles" change="+8.1% vs M-1" positive valueColor="emerald" />
    </div>

    {/* Charts */}
    <div className="grid md:grid-cols-5 gap-4">
      <div className="md:col-span-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border border-slate-200 dark:border-slate-700/30">
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">CA mensuel (FCFA M)</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/20">Sage 100 live</span>
        </div>
        <BarChart />
      </div>
      <div className="md:col-span-2 bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border border-slate-200 dark:border-slate-700/30">
        <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-3">AR Aging — Clients</span>
        <ARAgingChart />
      </div>
    </div>
  </div>
);

const KPICard = ({ label, value, unit, change, positive, progress, valueColor = 'white' }) => (
  <div className="bg-white dark:bg-slate-800/60 rounded-xl p-4 border border-slate-200 dark:border-slate-700/40 hover:border-blue-400 dark:hover:border-blue-500/30 transition-colors cursor-default shadow-sm dark:shadow-none">
    <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1.5">{label}</p>
    <p className={`text-xl font-bold ${valueColor === 'emerald' ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-900 dark:text-white'}`}>{value}</p>
    <p className="text-[11px] text-slate-500">{unit}</p>
    {change && (
      <div className={`mt-2 flex items-center gap-1 text-[11px] ${positive ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'} font-medium`}>
        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d={positive ? "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" : "M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"} />
        </svg>
        {change}
      </div>
    )}
    {progress && (
      <div className="mt-2 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full" style={{width: `${progress}%`}}></div>
      </div>
    )}
  </div>
);

const BarChart = () => {
  const data = [
    { month: 'Août', value: 680, height: '68%' },
    { month: 'Sep', value: 710, height: '71%' },
    { month: 'Oct', value: 695, height: '69%' },
    { month: 'Nov', value: 740, height: '74%' },
    { month: 'Déc', value: 760, height: '76%' },
    { month: 'Jan', value: 800, height: '80%' },
    { month: 'Fév', value: 820, height: '82%' },
    { month: 'Mar', value: 847, height: '100%', current: true }
  ];
  const barShades = [
    'bg-blue-500/20','bg-blue-500/25','bg-blue-500/30',
    'bg-blue-500/35','bg-blue-500/40','bg-blue-500/45','bg-blue-500/50'
  ];

  return (
    <div className="flex items-end gap-2 h-20">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <span className={`text-[9px] ${d.current ? 'text-emerald-600 dark:text-emerald-400 font-bold' : 'text-slate-500 dark:text-slate-600'}`}>{d.value}</span>
          <div
            className={`w-full rounded-sm ${d.current ? 'bg-blue-500' : (barShades[i] || 'bg-blue-500/20')}`}
            style={{height: d.height}}
          ></div>
          <span className={`text-[9px] ${d.current ? 'text-emerald-600 dark:text-emerald-400 font-bold' : 'text-slate-500 dark:text-slate-600'}`}>{d.month}</span>
        </div>
      ))}
    </div>
  );
};

const ARAgingChart = () => (
  <div className="space-y-2">
    <div>
      <div className="flex justify-between text-[11px] mb-1">
        <span className="text-slate-600 dark:text-slate-400">&lt; 30 jours</span>
        <span className="text-emerald-600 dark:text-emerald-400 font-semibold">68%</span>
      </div>
      <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full"><div className="h-full bg-emerald-500 rounded-full" style={{width:'68%'}}></div></div>
    </div>
    <div>
      <div className="flex justify-between text-[11px] mb-1">
        <span className="text-slate-600 dark:text-slate-400">30–60 jours</span>
        <span className="text-amber-600 dark:text-amber-400 font-semibold">22%</span>
      </div>
      <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full"><div className="h-full bg-amber-500 rounded-full" style={{width:'22%'}}></div></div>
    </div>
    <div>
      <div className="flex justify-between text-[11px] mb-1">
        <span className="text-slate-600 dark:text-slate-400">&gt; 60 jours</span>
        <span className="text-red-600 dark:text-red-400 font-semibold">10%</span>
      </div>
      <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full"><div className="h-full bg-red-500 rounded-full" style={{width:'10%'}}></div></div>
    </div>
    <div className="pt-2 border-t border-slate-200 dark:border-slate-700/50">
      <p className="text-[11px] text-slate-500">7 clients · retard &gt;60j</p>
      <p className="text-[11px] text-red-600 dark:text-red-400 font-medium">12.4M FCFA à recouvrer</p>
    </div>
  </div>
);

const NLQView = () => (
  <div className="p-5 md:p-6">
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-1">Posez votre question en français</h3>
        <p className="text-xs text-slate-500">Zuri comprend votre question, interroge votre base localement et affiche le résultat en quelques secondes</p>
      </div>

      <div className="bg-blue-50 dark:bg-slate-800/70 rounded-xl border border-blue-200 dark:border-blue-500/30 px-4 py-3 flex items-center gap-3 mb-6">
        <svg className="w-4 h-4 text-blue-500 dark:text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path></svg>
        <span className="text-sm text-slate-700 dark:text-slate-300 font-mono flex-1 cursor">"Quels clients ont un retard supérieur à 60 jours ?"</span>
        <span className="text-[10px] px-2 py-1 rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-600 dark:text-blue-400 whitespace-nowrap">Entrée ↵</span>
      </div>

      <div className="space-y-3 mb-6">
        <ProcessStep step={1} text={<>Question comprise : <span className="text-blue-600 dark:text-blue-400">retards de paiement clients</span></>} time="12ms" status="done" />
        <ProcessStep step={2} text={<>Consultation validée — <span className="text-emerald-600 dark:text-emerald-400">lecture seule</span>, périmètre autorisé ✓</>} time="8ms" status="done" />
        <ProcessStep step={3} text={<>Calculé localement par l'agent Sage 100 — <span className="text-emerald-600 dark:text-emerald-400">aucune donnée envoyée</span></>} time="1.4s" status="done" />
      </div>

      <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700/40 overflow-hidden mb-5 shadow-sm dark:shadow-none">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-700/40">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">Résultat — 7 clients</span>
          <div className="flex gap-2">
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/25">⚠ 2 critiques</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-transparent">Export CSV</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="border-b border-slate-100 dark:border-slate-700/40">
              <tr>
                <th className="text-left px-4 py-2 text-slate-500 font-medium">Client</th>
                <th className="text-right px-4 py-2 text-slate-500 font-medium">Retard (j)</th>
                <th className="text-right px-4 py-2 text-slate-500 font-medium">Montant (FCFA)</th>
                <th className="text-right px-4 py-2 text-slate-500 font-medium">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/30">
              <tr className="hover:bg-slate-50 dark:hover:bg-slate-700/20 transition-colors">
                <td className="px-4 py-2.5 text-slate-800 dark:text-slate-300 font-medium">PHARMACI DAKAR</td>
                <td className="px-4 py-2.5 text-right text-red-600 dark:text-red-400 font-bold">94j</td>
                <td className="px-4 py-2.5 text-right text-slate-700 dark:text-slate-300">5 820 000</td>
                <td className="px-4 py-2.5 text-right"><span className="px-1.5 py-0.5 rounded bg-red-500/15 text-red-600 dark:text-red-400">Critique</span></td>
              </tr>
              <tr className="hover:bg-slate-50 dark:hover:bg-slate-700/20 transition-colors">
                <td className="px-4 py-2.5 text-slate-800 dark:text-slate-300 font-medium">TOUBA TRANSIT</td>
                <td className="px-4 py-2.5 text-right text-red-600 dark:text-red-400 font-bold">81j</td>
                <td className="px-4 py-2.5 text-right text-slate-700 dark:text-slate-300">3 200 000</td>
                <td className="px-4 py-2.5 text-right"><span className="px-1.5 py-0.5 rounded bg-red-500/15 text-red-600 dark:text-red-400">Critique</span></td>
              </tr>
              <tr className="hover:bg-slate-50 dark:hover:bg-slate-700/20 transition-colors">
                <td className="px-4 py-2.5 text-slate-800 dark:text-slate-300 font-medium">SAHEL CONSTRUCT</td>
                <td className="px-4 py-2.5 text-right text-amber-600 dark:text-amber-400 font-bold">67j</td>
                <td className="px-4 py-2.5 text-right text-slate-700 dark:text-slate-300">1 950 000</td>
                <td className="px-4 py-2.5 text-right"><span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400">À suivre</span></td>
              </tr>
              <tr className="opacity-60">
                <td className="px-4 py-2.5 text-slate-500 italic" colSpan="4">+ 4 autres clients…</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/25">
        <span className="text-xs text-slate-600 dark:text-slate-400">Ajouter ce résultat à votre cockpit comme widget ?</span>
        <button className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors px-3 py-1.5 rounded-lg bg-blue-500/15 hover:bg-blue-500/25">
          + Ajouter au cockpit
        </button>
      </div>
    </div>
  </div>
);

const ProcessStep = ({ text, status, time }) => (
  <div className="flex items-center gap-3 text-xs">
    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${status === 'done' ? 'bg-emerald-500/20 border border-emerald-500/40' : 'bg-slate-200 dark:bg-slate-700 border border-slate-300 dark:border-slate-600'}`}>
      {status === 'done' && <svg className="w-3 h-3 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"></path></svg>}
    </div>
    <span className="text-slate-600 dark:text-slate-400">{text}</span>
    {time && <span className="ml-auto text-slate-500 dark:text-slate-600">{time}</span>}
  </div>
);

const KPIStoreView = () => {
  const packs = ['Finance', 'Trésorerie', 'Clients', 'Fournisseurs', 'Stocks', '+ 7 packs'];
  const kpiCards = [
    { name: "Chiffre d'affaires", tag: "Revenue · Finance", desc: "Total des ventes brutes sur la période. Agrégé depuis les pièces de ventes Sage.", direction: '↑ Plus = mieux', good: true, freq: 'Mensuel' },
    { name: "DSO — Délai moyen paiement", tag: "Days Sales Outstanding · Clients", desc: "Nombre moyen de jours pour encaisser les créances clients après émission de facture.", direction: '↓ Moins = mieux', good: false, freq: 'Mensuel' },
    { name: "EBITDA approx.", tag: "Résultat opérationnel · Finance", desc: "Estimation EBITDA à partir des comptes de gestion Sage. Indicateur clé de rentabilité.", direction: '↑ Plus = mieux', good: true, freq: 'Mensuel' },
  ];

  return (
    <div className="p-5 md:p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-bold text-slate-900 dark:text-white">KPI Store</h3>
          <p className="text-xs text-slate-500 mt-0.5">126 KPIs préconfigurés · 12 packs métiers</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm dark:shadow-none">
          <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
          <span className="text-xs text-slate-500">Rechercher un KPI…</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {packs.map((p, i) => (
          <span key={p} className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors ${i === 0 ? 'font-semibold bg-blue-500/20 border border-blue-500/40 text-blue-600 dark:text-blue-400' : 'bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:border-slate-300 dark:hover:border-slate-600'}`}>{p}</span>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        {kpiCards.map((k) => (
          <div key={k.name} className="bg-white dark:bg-slate-800/60 rounded-xl p-4 border border-slate-200 dark:border-slate-700/40 hover:border-blue-400 dark:hover:border-blue-500/35 transition-colors cursor-default group shadow-sm dark:shadow-none">
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="text-xs font-bold text-slate-900 dark:text-white">{k.name}</p>
                <p className="text-[10px] text-slate-500">{k.tag}</p>
              </div>
              <svg className="w-4 h-4 text-emerald-500 dark:text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"></path></svg>
            </div>
            <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed">{k.desc}</p>
            <div className="flex items-center gap-2 mt-3">
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${k.good ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' : 'bg-red-500/15 text-red-600 dark:text-red-400'}`}>{k.direction}</span>
              <span className="text-[10px] text-slate-500 dark:text-slate-600">{k.freq}</span>
            </div>
          </div>
        ))}
      </div>

      <p className="text-center text-xs text-slate-500 dark:text-slate-600 mt-4">123 autres KPIs disponibles dans le store complet</p>
    </div>
  );
};

// Features Section
const FeaturesSection = () => (
  <section id="features" className="bg-navy-800 py-24" data-testid="features-section">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16 reveal">
        <span className="text-xs text-blue-400 uppercase tracking-widest font-semibold">Fonctionnalités</span>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-3 mb-4">
          Chaque fonctionnalité résout<br className="hidden sm:block"/>un vrai problème de terrain.
        </h2>
        <p className="text-slate-400 text-lg max-w-xl mx-auto">Pas des cases à cocher. Des outils que vos DAFs utilisent chaque matin.</p>
      </div>

      {/* Big NLQ feature */}
      <div className="glass-card rounded-2xl overflow-hidden mb-6 reveal">
        <div className="grid md:grid-cols-2 gap-0">
          <div className="p-8 md:p-10 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-xs text-blue-600 dark:text-blue-400 font-semibold mb-5 w-fit">
              Requêtes en langage naturel
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3 leading-snug">
              Posez une question.<br/>Obtenez la réponse en 2 secondes.
            </h3>
            <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed mb-6">
              Fini les tickets à la DSI. Posez votre question en français directement dans l'interface — Zuri, notre IA, comprend votre besoin, interroge votre base localement et affiche la réponse en quelques secondes.
            </p>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                <svg className="w-4 h-4 text-blue-500 dark:text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"></path></svg>
                Posez vos questions en français : chiffre d'affaires, trésorerie, marges — Zuri, notre IA, répond instantanément.
              </li>
              <li className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                <svg className="w-4 h-4 text-blue-500 dark:text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"></path></svg>
                Vos données ne sortent jamais de vos locaux.
              </li>
              <li className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                <svg className="w-4 h-4 text-blue-500 dark:text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"></path></svg>
                Vos analyses restent accessibles, sans ressaisie.
              </li>
            </ul>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900/60 border-l border-slate-200 dark:border-slate-700/40 p-6 flex flex-col gap-3 justify-center">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Questions suggérées</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-white dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700/50 hover:border-blue-400 dark:hover:border-blue-500/40 transition-colors cursor-pointer group shadow-sm dark:shadow-none">
                <svg className="w-3.5 h-3.5 text-slate-400 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path></svg>
                <span className="text-xs text-slate-700 dark:text-slate-300 font-mono">"CA janvier vs janvier N-1 ?"</span>
              </div>
              <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-blue-50 dark:bg-blue-500/10 border border-blue-300 dark:border-blue-500/35 cursor-pointer group">
                <svg className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path></svg>
                <span className="text-xs text-blue-700 dark:text-blue-300 font-mono">"Clients avec retard &gt; 60j ?"</span>
                <span className="ml-auto text-[9px] text-blue-600 dark:text-blue-500">actif</span>
              </div>
              <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-white dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700/50 hover:border-blue-400 dark:hover:border-blue-500/40 transition-colors cursor-pointer group shadow-sm dark:shadow-none">
                <svg className="w-3.5 h-3.5 text-slate-400 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path></svg>
                <span className="text-xs text-slate-700 dark:text-slate-300 font-mono">"Top 10 fournisseurs par volume ?"</span>
              </div>
              <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-white dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700/50 hover:border-blue-400 dark:hover:border-blue-500/40 transition-colors cursor-pointer group shadow-sm dark:shadow-none">
                <svg className="w-3.5 h-3.5 text-slate-400 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path></svg>
                <span className="text-xs text-slate-700 dark:text-slate-300 font-mono">"EBITDA approx. ce trimestre ?"</span>
              </div>
            </div>
            <div className="mt-2 pt-4 border-t border-slate-200 dark:border-slate-700/40">
              <div className="flex items-center gap-2 text-[10px] text-slate-500">
                <span className="px-2 py-0.5 rounded bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/20 font-mono">Votre question</span>
                <span>→</span>
                <span className="px-2 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400 font-mono">Traitement local</span>
                <span>→</span>
                <span className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20 font-mono">Réponse</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2x2 grid */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="glass-card rounded-2xl p-7 reveal">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path></svg>
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900 dark:text-white mb-1">126 KPIs prêts à l'emploi</h3>
              <p className="text-xs text-slate-500">Aucune configuration requise</p>
            </div>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-5">
            Pas besoin de définir vos propres métriques. Chaque KPI est préconstruit avec sa formule, son unité, sa fréquence et son indicateur de direction. Activez un pack métier et vos KPIs s'affichent instantanément.
          </p>
          <div className="grid grid-cols-3 gap-2">
            {[['Finance','18 KPIs'],['Trésorerie','12 KPIs'],['Clients','15 KPIs'],['Fournisseurs','14 KPIs'],['Stocks','11 KPIs']].map(([n,k]) => (
              <div key={n} className="text-center px-2 py-2.5 rounded-lg bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/40">
                <p className="text-[10px] text-slate-500 mb-0.5">{n}</p>
                <p className="text-sm font-bold text-blue-600 dark:text-blue-400">{k}</p>
              </div>
            ))}
            <div className="text-center px-2 py-2.5 rounded-lg bg-blue-500/10 border border-blue-500/25">
              <p className="text-[10px] text-slate-500 mb-0.5">+ 7 packs</p>
              <p className="text-sm font-bold text-blue-600 dark:text-blue-400">56 KPIs</p>
            </div>
          </div>
        </div>

        <div className="glass-card rounded-2xl p-7 reveal">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-11 h-11 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"></path></svg>
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900 dark:text-white mb-1">Chaque rôle a son cockpit</h3>
              <p className="text-xs text-slate-500">Drag-and-drop · Packs préconfigurés</p>
            </div>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-5">
            Le DAF ne voit pas la même chose que le contrôleur ou l'analyste. Chaque utilisateur construit son propre tableau de bord avec les widgets qui lui parlent. Les packs préconfigurés (DAF, DG, Contrôleur) offrent un cockpit prêt en un clic.
          </p>
          <div className="space-y-2">
            {[
              ['D','Pack DAF','CA, Marge, DSO, Trésorerie, EBITDA, DPO','bg-blue-500/20 border-blue-500/30 text-blue-600 dark:text-blue-400'],
              ['C','Pack Contrôleur','Budgets, écarts, analytique, stocks','bg-emerald-500/20 border-emerald-500/30 text-emerald-700 dark:text-emerald-400'],
              ['A','Pack Analyste','Tendances, prévisions, AR aging, ML','bg-purple-500/20 border-purple-500/30 text-purple-700 dark:text-purple-400']
            ].map(([i,n,d,dotCls]) => (
              <div key={n} className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/40">
                <div className="flex items-center gap-2.5">
                  <div className={`w-6 h-6 rounded-full border flex items-center justify-center text-[9px] font-bold ${dotCls}`}>{i}</div>
                  <span className="text-xs text-slate-800 dark:text-slate-300">{n}</span>
                </div>
                <span className="text-[10px] text-slate-500">{d}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Row of 3 */}
      <div className="grid md:grid-cols-3 gap-5">
        <div className="glass-card rounded-2xl p-6 reveal">
          <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center mb-4">
            <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
          </div>
          <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-2">Contrôle d'accès granulaire</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
            5 rôles prédéfinis avec permissions par ressource. L'analyste voit les données, mais ne peut pas modifier les objectifs KPI. Le contrôleur exporte, mais ne gère pas la facturation.
          </p>
          <div className="flex flex-wrap gap-1.5">
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400">Owner</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400">DAF</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400">Contrôleur</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400">Analyste</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/20">+ Custom</span>
          </div>
        </div>

        <div className="glass-card rounded-2xl p-6 reveal">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-4">
            <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
          </div>
          <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-2">Opérationnel en 30 minutes</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
            Un parcours guidé en 3 étapes : inscription → déploiement agent → activation KPIs et invitations. Première donnée visible en moins de 30 minutes.
          </p>
          <div className="flex items-center gap-1">
            <div className="flex-1 h-1.5 bg-emerald-500 rounded-full"></div>
            <div className="flex-1 h-1.5 bg-emerald-500 rounded-full"></div>
            <div className="flex-1 h-1.5 bg-blue-500 rounded-full"></div>
          </div>
          <p className="text-[10px] text-slate-500 mt-1.5">Étape 3/3 — Activation KPI packs</p>
        </div>

        <div className="glass-card rounded-2xl p-6 reveal">
          <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-4">
            <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"></path></svg>
          </div>
          <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-2">Audit log complet &amp; conformité</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
            Chaque action est tracée : connexions, exports, modifications, questions posées. Les données personnelles (emails, mots de passe) sont masquées automatiquement. Exportable pour vos audits internes.
          </p>
          <div className="space-y-1.5 font-mono">
            <div className="flex gap-2 text-[10px]">
              <span className="text-slate-500">09:14</span>
              <span className="text-emerald-600 dark:text-emerald-400">CONNEXION</span>
              <span className="text-slate-600 dark:text-slate-400">a.kon***@ph***ma.sn</span>
            </div>
            <div className="flex gap-2 text-[10px]">
              <span className="text-slate-500">09:16</span>
              <span className="text-blue-600 dark:text-blue-400">QUESTION</span>
              <span className="text-slate-600 dark:text-slate-400">"Quelle est ma balance âgée clients ce mois-ci ?"</span>
            </div>
            <div className="flex gap-2 text-[10px]">
              <span className="text-slate-500">09:18</span>
              <span className="text-amber-600 dark:text-amber-400">EXPORT</span>
              <span className="text-slate-600 dark:text-slate-400">dashboard_mars_2026.pdf</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Agent Section
const AgentSection = () => {
  return (
    <section id="agent" className="bg-navy-800 py-24" data-testid="agent-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 reveal">
          <span className="text-xs text-emerald-400 uppercase tracking-widest font-semibold">L'agent Cockpit</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-3 mb-4">
            Un agent Windows natif.<br className="hidden sm:block"/>Installé chez vous. Rien de caché.
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Un installeur graphique, un service Windows, un dashboard de santé local. Vous voyez exactement ce qu'il fait, en temps réel, sur votre propre poste.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-start">
          {/* Left: capabilities */}
          <div className="space-y-3 reveal">
            <AgentFeature
              icon="wizard"
              title="Installation simple"
              description="Un assistant d'installation guidé teste votre connexion en direct et s'adapte automatiquement à votre version de Sage 100."
            />
            <AgentFeature
              icon="service"
              title="Fonctionne en continu"
              description="Fonctionne comme un service système standard : démarre avec votre machine, se relance seul en cas d'incident."
            />
            <AgentFeature
              icon="views"
              title="Base intouchée"
              description="Accès strictement en lecture seule — aucune écriture, aucune modification possible sur votre base."
            />
            <AgentFeature
              icon="watermark"
              title="Aucune surcharge"
              description="Seules les données nouvelles sont transmises à chaque cycle — jamais de réextraction complète de votre base."
            />
            <AgentFeature
              icon="vault"
              title="Identifiants protégés"
              description="Vos identifiants sont chiffrés et stockés selon les standards de sécurité Windows, liés exclusivement à votre poste."
            />
          </div>

          {/* Right: local health dashboard mockup */}
          <div className="reveal" style={{filter: 'drop-shadow(0 0 50px rgba(16,185,129,0.10))'}}>
            <div className="glass rounded-2xl overflow-hidden border border-emerald-200 dark:border-emerald-500/15">
              <div className="flex items-center justify-between px-4 py-3 bg-slate-100 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-700/50">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400/70"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400/70"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400/70"></div>
                </div>
                <span className="text-[11px] text-slate-500 dark:text-slate-400 font-mono">Cockpit — Agent On-Premise</span>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                  <span className="text-[11px] text-emerald-600 dark:text-emerald-400">Opérationnel</span>
                </div>
              </div>

              <div className="p-5">
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-white dark:bg-slate-800/60 rounded-xl p-3 border border-slate-200 dark:border-slate-700/40 shadow-sm dark:shadow-none">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">En service depuis</p>
                    <p className="text-sm font-bold text-slate-900 dark:text-white">1h 00m 12s</p>
                  </div>
                  <div className="bg-white dark:bg-slate-800/60 rounded-xl p-3 border border-emerald-200 dark:border-emerald-500/20 shadow-sm dark:shadow-none">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Base Sage 100</p>
                    <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">Connectée</p>
                  </div>
                  <div className="bg-white dark:bg-slate-800/60 rounded-xl p-3 border border-emerald-200 dark:border-emerald-500/20 shadow-sm dark:shadow-none">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Cockpit Cloud</p>
                    <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">Connecté</p>
                  </div>
                </div>

                <div className="bg-emerald-50 dark:bg-slate-800/50 rounded-xl border border-emerald-200 dark:border-emerald-500/20 p-4 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                    <span className="text-[12px] font-semibold text-emerald-700 dark:text-emerald-400">Agent connecté · Sage 100</span>
                  </div>
                  <div className="space-y-2 text-[11px]">
                    <div className="flex items-center justify-between text-slate-600 dark:text-slate-400">
                      <span>Dernière synchronisation</span>
                      <span className="text-emerald-600 dark:text-emerald-400 font-medium">il y a 28 s</span>
                    </div>
                    <div className="flex items-start gap-2 text-slate-600 dark:text-slate-400">
                      <span className="flex-shrink-0">6 domaines suivis</span>
                      <span className="text-slate-500">Ventes · Grand livre · Clients · Trésorerie · Stocks · Paie</span>
                    </div>
                  </div>
                </div>

                <p className="text-[10px] text-slate-500 text-center">Accessible depuis n'importe quel navigateur sur le réseau local — aucune donnée n'en sort.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const AgentFeature = ({ icon, title, description }) => {
  const icons = {
    wizard: "M13 10V3L4 14h7v7l9-11h-7z",
    service: "M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z",
    views: "M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z",
    watermark: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
    vault: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z",
  };

  return (
    <div className="flex items-start gap-4 p-4 rounded-xl bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700/40 hover:border-emerald-400 dark:hover:border-emerald-500/25 transition-colors shadow-sm dark:shadow-none">
      <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
        <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d={icons[icon]}></path></svg>
      </div>
      <div>
        <p className="text-sm font-semibold text-slate-900 dark:text-white mb-0.5">{title}</p>
        <p className="text-xs text-slate-600 dark:text-slate-500">{description}</p>
      </div>
    </div>
  );
};

// How It Works Section
const HowItWorksSection = () => (
  <section id="how-it-works" className="bg-navy-900 py-24" data-testid="how-it-works-section">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16 reveal">
        <span className="text-xs text-blue-400 uppercase tracking-widest font-semibold">Déploiement</span>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-3 mb-4">
          De l'inscription au premier KPI<br className="hidden sm:block"/>en moins de 30 minutes.
        </h2>
        <p className="text-slate-400 text-lg max-w-xl mx-auto">Un parcours guidé en 3 étapes. Aucune compétence technique requise.</p>
      </div>

      <div className="space-y-5">
        {/* Step 1 */}
        <div className="glass-card rounded-2xl p-6 md:p-8 reveal">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-extrabold text-lg" style={{boxShadow:'0 0 20px rgba(59,130,246,0.4)'}}>1</div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-base font-bold text-white">Créez votre organisation</h3>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-700 text-slate-400">~5 min</span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed mb-4">Inscrivez-vous, choisissez votre plan et complétez le profil de votre organisation. Cockpit crée instantanément votre espace dédié et sécurisé.</p>
              <div className="flex flex-wrap gap-2">
                <span className="text-[11px] px-2.5 py-1 rounded-lg bg-slate-800 border border-slate-700 text-slate-400">Choix du plan</span>
                <span className="text-[11px] px-2.5 py-1 rounded-lg bg-slate-800 border border-slate-700 text-slate-400">Profil organisation</span>
                <span className="text-[11px] px-2.5 py-1 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400">Espace prêt</span>
              </div>
            </div>
          </div>
        </div>

        {/* Step 2 */}
        <div className="glass-card rounded-2xl p-6 md:p-8 reveal">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-emerald-500 flex items-center justify-center text-white font-extrabold text-lg" style={{boxShadow:'0 0 20px rgba(16,185,129,0.3)'}}>2</div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-base font-bold text-white">Déployez l'agent sur votre infrastructure</h3>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-700 text-slate-400">~15 min</span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed mb-4">L'agent Cockpit s'installe en quelques clics sur un poste ou serveur de votre réseau. Il se connecte localement à votre base Sage 100 et n'accède qu'en lecture — vos données ne quittent jamais votre infrastructure. Voir le détail dans la section <a href="#agent" className="text-blue-400 hover:text-blue-300">Agent Cockpit</a>.</p>
              <div className="flex flex-wrap gap-2">
                <span className="text-[11px] px-2.5 py-1 rounded-lg bg-slate-800 border border-slate-700 text-slate-400">Installation guidée</span>
                <span className="text-[11px] px-2.5 py-1 rounded-lg bg-slate-800 border border-slate-700 text-slate-400">Connexion Sage 100</span>
                <span className="text-[11px] px-2.5 py-1 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400">Lecture seule · aucune écriture</span>
              </div>
            </div>
          </div>
        </div>

        {/* Step 3 */}
        <div className="glass-card rounded-2xl p-6 md:p-8 reveal">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white font-extrabold text-lg" style={{boxShadow:'0 0 20px rgba(16,185,129,0.4)'}}>3</div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-base font-bold text-white">Activez vos KPI packs &amp; invitez votre équipe</h3>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">Prêt</span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed mb-4">Choisissez les packs métiers adaptés à votre profil. Invitez vos collaborateurs avec leur rôle prédéfini. Chacun accède à son cockpit personnalisé — vos premiers KPIs sont visibles immédiatement.</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-800/60 rounded-xl p-3 border border-emerald-500/20 text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                    <span className="text-[10px] text-emerald-400 font-semibold">Agent</span>
                  </div>
                  <p className="text-xs text-slate-400">Online · 28s</p>
                </div>
                <div className="bg-slate-800/60 rounded-xl p-3 border border-blue-500/20 text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-400"></span>
                    <span className="text-[10px] text-blue-400 font-semibold">KPIs</span>
                  </div>
                  <p className="text-xs text-slate-400">8 packs · 96 actifs</p>
                </div>
                <div className="bg-slate-800/60 rounded-xl p-3 border border-purple-500/20 text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-400"></span>
                    <span className="text-[10px] text-purple-400 font-semibold">Équipe</span>
                  </div>
                  <p className="text-xs text-slate-400">4 invités · actifs</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Security Section
const SecuritySection = () => (
  <section id="security" className="bg-navy-800 py-24" data-testid="security-section">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16 reveal">
        <span className="text-xs text-emerald-400 uppercase tracking-widest font-semibold">Sécurité</span>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-3 mb-4">
          Vos données ne quittent jamais vos locaux.
        </h2>
        <p className="text-slate-400 text-lg max-w-xl mx-auto">Principes immuables. Aucun compromis.</p>
      </div>

      <div className="max-w-2xl mx-auto space-y-3 reveal">
        <p className="text-sm font-semibold text-slate-300 mb-4">5 principes immuables</p>
        <SecurityPrinciple type="deny" title="Aucune connexion entrante vers votre réseau" description="Cockpit ne peut jamais initier de connexion vers votre infrastructure, c'est toujours votre agent qui communique vers l'extérieur, jamais l'inverse." />
        <SecurityPrinciple type="deny" title="Aucune copie de votre base dans le cloud" description="Vos tables Sage ne sont jamais répliquées. Chaque indicateur est calculé à la demande, localement, puis transmis sous forme de résultat agrégé." />
        <SecurityPrinciple type="deny" title="Votre mot de passe Sage ne quitte jamais vos locaux" description="Votre mot de passe Sage ne nous est jamais transmis. L'agent gère la connexion directement, chez vous." />
        <SecurityPrinciple type="allow" title="Accès en lecture stricte, aucune écriture possible" description="Chaque requête est strictement limitée à la consultation, sur un périmètre de données défini à l'avance." />
        <SecurityPrinciple type="allow" title="Traçabilité complète et droits d'accès par rôle" description="Chaque action est tracée et horodatée. Les données personnelles sont automatiquement masquées dans les journaux." />

        <div className="flex flex-wrap gap-2 pt-2">
          <span className="text-[11px] px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 font-medium">Mots de passe chiffrés</span>
          <span className="text-[11px] px-3 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/25 text-blue-400 font-medium">Connexions chiffrées de bout en bout</span>
          <span className="text-[11px] px-3 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/25 text-purple-400 font-medium">Environnement dédié par client</span>
          <span className="text-[11px] px-3 py-1.5 rounded-lg bg-slate-700 text-slate-400 font-medium">Protection anti-intrusion</span>
          <span className="text-[11px] px-3 py-1.5 rounded-lg bg-slate-700 text-slate-400 font-medium">Standards de sécurité web</span>
        </div>
      </div>
    </div>
  </section>
);

const SecurityPrinciple = ({ type, title, description }) => (
  <div className={`flex items-start gap-4 p-4 rounded-xl bg-slate-800/40 border border-slate-700/40 hover:border-${type === 'deny' ? 'red' : 'emerald'}-500/25 transition-colors`}>
    <div className={`w-8 h-8 rounded-lg ${type === 'deny' ? 'bg-red-500/10 border-red-500/20' : 'bg-emerald-500/10 border-emerald-500/20'} border flex items-center justify-center flex-shrink-0`}>
      {type === 'deny' ? (
        <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"></path></svg>
      ) : (
        <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"></path></svg>
      )}
    </div>
    <div>
      <p className="text-sm font-semibold text-white mb-0.5">{title}</p>
      <p className="text-xs text-slate-500">{description}</p>
    </div>
  </div>
);

// Pricing Section
const PricingSection = () => (
  <section id="pricing" className="bg-navy-900 py-24" data-testid="pricing-section">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-14 reveal">
        <span className="text-xs text-blue-400 uppercase tracking-widest font-semibold">Tarifs</span>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-3 mb-4">
          Transparent. En FCFA. Sans surprise.
        </h2>
        <p className="text-slate-400 text-lg max-w-xl mx-auto">Facturation mensuelle. Résiliation à tout moment. Paiement via Flutterwave.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 items-start">
        <PricingCard 
          name="Essentiel"
          price="36 000"
          usd="55"
          description="PME en phase de démarrage"
          features={[
            { text: '3 utilisateurs', included: true },
            { text: '6 KPIs · 10 widgets', included: true },
            { text: 'Packs Finance & Trésorerie', included: true },
            { text: '1 agent Sage', included: true },
            { text: 'Requêtes NLQ', included: false },
            { text: 'Rapports avancés', included: false }
          ]}
        />
        <PricingCard 
          name="Business"
          price="100 000"
          usd="150"
          description="PME en croissance"
          popular
          features={[
            { text: '10 utilisateurs', included: true },
            { text: 'KPIs & widgets illimités', included: true },
            { text: 'Requêtes NLQ activées', included: true, highlight: true },
            { text: '8 packs KPI', included: true },
            { text: 'Rapports avancés', included: true },
            { text: 'Syncs agents illimitées', included: true }
          ]}
        />
        <PricingCard 
          name="Enterprise"
          price="300 000"
          usd="450"
          description="Groupes multi-Sage"
          features={[
            { text: 'Utilisateurs illimités', included: true },
            { text: 'Tout illimité', included: true },
            { text: 'NLQ + Rapports avancés', included: true },
            { text: '12 packs KPI (tous)', included: true, highlight: true },
            { text: 'Multi-instances Sage', included: true },
            { text: 'Support prioritaire dédié', included: true }
          ]}
          enterprise
        />
      </div>

      <div className="flex items-center justify-center gap-2 mt-8 reveal">
        <svg className="w-4 h-4 text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
        <p className="text-xs text-slate-500">Paiement sécurisé via Flutterwave · Facturation mensuelle · Résiliation sans frais à tout moment</p>
      </div>
    </div>
  </section>
);

const PricingCard = ({ name, price, usd, description, features, popular, enterprise }) => (
  <div 
    className={`rounded-2xl p-7 reveal relative ${
      popular 
        ? '' 
        : 'glass-card'
    }`}
    style={popular ? {
      background: 'linear-gradient(145deg,rgba(13,26,55,0.95),rgba(10,22,48,0.95))',
      border: '1px solid rgba(59,130,246,0.45)',
      boxShadow: '0 0 50px rgba(59,130,246,0.12),0 24px 48px rgba(0,0,0,0.4)'
    } : {}}
  >
    {popular && (
      <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
        <span className="px-4 py-1 rounded-full text-xs font-bold text-white" style={{background:'linear-gradient(135deg,#3b82f6,#10b981)'}}>Le plus populaire</span>
      </div>
    )}
    <p className={`text-xs font-semibold uppercase tracking-wide mb-4 ${popular ? 'text-blue-400 mt-1' : 'text-slate-500'}`}>{name}</p>
    <div className="mb-1">
      <span className="text-4xl font-extrabold text-white">{price}</span>
      <span className={`text-sm ml-1 ${popular ? 'text-slate-400' : 'text-slate-500'}`}>FCFA / mois</span>
    </div>
    <p className="text-xs text-slate-600 mb-6">≈ {usd} USD · {description}</p>
    <ul className="space-y-3 mb-8 text-sm">
      {features.map((f, i) => (
        <li key={i} className={`flex items-center gap-2.5 ${f.included ? (popular ? 'text-slate-200' : 'text-slate-300') : 'text-slate-500'} ${f.highlight ? 'font-medium' : ''}`}>
          {f.included ? (
            <svg className={`w-4 h-4 flex-shrink-0 ${popular ? 'text-emerald-400' : 'text-blue-400'}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"></path></svg>
          ) : (
            <svg className="w-4 h-4 text-slate-700 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"></path></svg>
          )}
          {f.text}
        </li>
      ))}
    </ul>
    {enterprise ? (
      <a href="mailto:contact@nafakatech.com" className="block w-full text-center text-sm font-semibold text-slate-300 py-3 rounded-xl border border-slate-700 hover:border-slate-500 hover:bg-slate-800/50 transition-all duration-200" data-testid="pricing-enterprise-cta">
        Nous contacter
      </a>
    ) : popular ? (
      <a href="https://cockpit.nafakatech.com/register" target="_blank" rel="noopener noreferrer" className="block w-full text-center text-sm font-bold text-white py-3 rounded-xl transition-all duration-200" style={{background:'linear-gradient(135deg,#3b82f6,#2563eb)',boxShadow:'0 4px 20px rgba(59,130,246,0.4)'}} data-testid="pricing-business-cta">
        Commencer — Business
      </a>
    ) : (
      <a href="https://cockpit.nafakatech.com/register" target="_blank" rel="noopener noreferrer" className="block w-full text-center text-sm font-semibold text-slate-300 py-3 rounded-xl border border-slate-700 hover:border-slate-500 hover:bg-slate-800/50 transition-all duration-200" data-testid="pricing-essentiel-cta">
        Commencer
      </a>
    )}
  </div>
);

// Testimonials Section
const TestimonialsSection = () => (
  <section id="testimonials" className="bg-navy-800 py-24" data-testid="testimonials-section">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-14 reveal">
        <span className="text-xs text-blue-400 uppercase tracking-widest font-semibold">Témoignages</span>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-3 mb-4">
          Ce que disent les DAFs qui l'utilisent.
        </h2>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <TestimonialCard 
          quote="Avant Cockpit, je passais 2 heures chaque lundi à consolider nos KPIs dans Excel depuis Sage 100. Maintenant mon cockpit DAF se met à jour automatiquement. J'ai récupéré ces 2 heures pour vraiment analyser — pas juste copier-coller."
          name="Aminata Konaté"
          role="DAF · Groupe Pharma — Ouagadougou"
          initials="AK"
          color="blue"
          stars={5}
        />
        <TestimonialCard 
          quote="La fonctionnalité NLQ m'a bluffé. J'ai tapé 'liste des fournisseurs avec DPO supérieur à 90 jours' et j'ai le tableau en moins de 3 secondes. Plus jamais de ticket à la DSI pour une requête Sage. Notre contrôleur est complètement autonome maintenant."
          name="Félix Diabaté"
          role="Directeur Général · Agro-industrie — Abidjan"
          initials="FD"
          color="emerald"
          stars={5}
          featured
        />
        <TestimonialCard 
          quote="La sécurité était notre frein numéro un. Notre DG refusait tout outil qui 'envoie nos données Sage dans le cloud'. L'architecture on-premise de Cockpit a tout débloqué. L'audit log complet nous donne la preuve que rien ne sort."
          name="Mariétou Badolo"
          role="Contrôleur de Gestion · Telecom — Ouagadougou"
          initials="MB"
          color="purple"
          stars={4}
        />
      </div>
    </div>
  </section>
);

const TestimonialCard = ({ quote, name, role, initials, color, stars, featured }) => {
  const colorGradients = {
    blue: 'from-blue-400 to-blue-600',
    emerald: 'from-emerald-400 to-emerald-600',
    purple: 'from-purple-400 to-purple-600'
  };

  return (
    <div className={`glass-card rounded-2xl p-6 reveal flex flex-col ${featured ? 'border-blue-500/20' : ''}`} style={featured ? {borderColor: 'rgba(59,130,246,0.22)'} : {}}>
      <div className="flex gap-0.5 mb-5">
        {[...Array(5)].map((_, i) => (
          <svg key={i} className={`w-4 h-4 ${i < stars ? 'text-amber-400' : 'text-slate-600'}`} fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
        ))}
      </div>
      <blockquote className="text-sm text-slate-300 leading-relaxed mb-6 flex-1 italic">
        "{quote}"
      </blockquote>
      <div className="flex items-center gap-3 pt-4 border-t border-slate-700/50">
        <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${colorGradients[color]} flex items-center justify-center text-sm font-bold text-white flex-shrink-0`}>{initials}</div>
        <div>
          <p className="text-sm font-semibold text-white">{name}</p>
          <p className="text-xs text-slate-500">{role}</p>
        </div>
      </div>
    </div>
  );
};

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "Mes données Sage sont-elles copiées sur vos serveurs ?",
      answer: "Non — jamais. L'agent installé sur votre infrastructure interroge localement votre Sage et retourne uniquement des résultats agrégés (KPIs calculés). Vos tables comptables, vos écritures, vos tiers — aucune de ces données n'est transmise ni stockée dans notre cloud."
    },
    {
      question: "Quelles versions de Sage sont supportées ?",
      answer: "Cockpit est nativement compatible avec Sage 100, en version locale (serveur on-premise) ou cloud privé. La compatibilité Sage X3 est en cours de déploiement. Le plan Enterprise supporte plusieurs instances Sage simultanément — idéal pour les groupes avec filiales."
    },
    {
      question: "Combien de temps prend le déploiement initial ?",
      answer: "Moins de 30 minutes pour la majorité de nos clients. Le parcours guidé couvre : création de votre organisation (~5 min) → installation de l'agent sur un poste de votre réseau (~15 min) → activation des packs KPI et invitation de votre équipe (~10 min). L'agent doit être connecté avant l'activation du cockpit — son état est visible en temps réel dans l'interface."
    },
    {
      question: "Puis-je changer de plan ou résilier à tout moment ?",
      answer: "Oui. Facturation mensuelle, sans engagement annuel. Upgrade immédiat en montant de plan. Résiliation depuis votre espace admin — effet en fin de période. Vos dashboards et configurations sont exportables pendant 30 jours après résiliation."
    },
    {
      question: "Zuri comprend-elle vraiment les questions en français ?",
      answer: "Oui. Le français est la langue principale de Zuri, notre moteur de questions. L'anglais est aussi supporté. Zuri fonctionne par détection d'intention sur des templates préconfigurés — les réponses sont rapides (<2s), prévisibles et sécurisées : consultation uniquement, périmètre de données défini à l'avance."
    }
  ];

  return (
    <section id="faq" className="bg-navy-900 py-24" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 reveal">
          <span className="text-xs text-blue-400 uppercase tracking-widest font-semibold">FAQ</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-3 mb-4">Questions fréquentes</h2>
          <p className="text-slate-400">Les vraies questions que posent les DAFs avant de signer.</p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div 
              key={i} 
              className={`faq-item glass-card rounded-xl overflow-hidden cursor-pointer ${openIndex === i ? 'open' : ''}`}
              onClick={() => setOpenIndex(openIndex === i ? null : i)}
              data-testid={`faq-item-${i}`}
            >
              <div className="flex items-center justify-between p-5 gap-4">
                <h3 className="text-sm font-semibold text-white leading-snug">{faq.question}</h3>
                <svg className="faq-icon w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-300" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"></path></svg>
              </div>
              <div className="faq-body">
                <p className="px-5 pb-5 text-sm text-slate-400 leading-relaxed">{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection = () => (
  <section className="bg-navy-800 py-24 relative overflow-hidden" data-testid="final-cta-section">
    <div className="orb w-[500px] h-[500px] bg-blue-600 opacity-[.08]" style={{top:'-20%',left:'10%'}} aria-hidden="true"></div>
    <div className="orb w-[400px] h-[400px] bg-emerald-500 opacity-[.07]" style={{bottom:'-20%',right:'10%'}} aria-hidden="true"></div>

    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 reveal">
      <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full glass border border-blue-500/30 text-xs font-medium text-slate-300 mb-7">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" aria-hidden="true"></span>
        Parlons de votre déploiement
      </div>

      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-6 leading-tight">
        Vos données Sage méritent<br/>mieux qu'un fichier Excel.
      </h2>

      <p className="text-slate-400 text-lg mb-10 max-w-xl mx-auto leading-relaxed">
        Pilotez la performance financière de votre entreprise avec Cockpit. Premier KPI visible en 30 minutes. Données 100% on-premise.
      </p>

      <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
        <button onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })} className="btn-primary inline-flex items-center justify-center gap-2 text-white font-bold px-8 py-4 rounded-xl text-base" data-testid="final-cta-primary">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
          Demander une démo
        </button>
        <a href="mailto:contact@nafakatech.com" className="btn-ghost inline-flex items-center justify-center gap-2 text-slate-300 font-medium px-8 py-4 rounded-xl text-base" data-testid="final-cta-secondary">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
          Parler à l'équipe
        </a>
      </div>

      <div className="flex flex-wrap justify-center gap-6 text-xs text-slate-500">
        <div className="flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"></path></svg>
          Données 100% on-premise
        </div>
        <div className="flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"></path></svg>
          Support en français
        </div>
        <div className="flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"></path></svg>
          Déploiement accompagné
        </div>
        <div className="flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"></path></svg>
          Devis sur mesure
        </div>
      </div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="bg-navy-950 border-t border-slate-800/60 pt-16 pb-8" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
        <div className="col-span-2 md:col-span-1">
          <CockpitLogo className="mb-4 w-fit" />
          <p className="text-xs text-slate-600 leading-relaxed mb-5">Votre ERP devient un cockpit financier.</p>
          <div className="flex gap-2">
            <a href="#" className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700/50 flex items-center justify-center text-slate-500 hover:text-white hover:border-slate-600 transition-colors" aria-label="LinkedIn">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"></path></svg>
            </a>
            <a href="#" className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700/50 flex items-center justify-center text-slate-500 hover:text-white hover:border-slate-600 transition-colors" aria-label="Twitter / X">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path></svg>
            </a>
          </div>
        </div>
        <div>
          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Produit</h4>
          <ul className="space-y-2.5">
            <li><a href="/#features" className="text-sm text-slate-600 hover:text-slate-300 transition-colors">Fonctionnalités</a></li>
            <li><a href="/#agent" className="text-sm text-slate-600 hover:text-slate-300 transition-colors">Agent</a></li>
            <li><a href="/#security" className="text-sm text-slate-600 hover:text-slate-300 transition-colors">Sécurité</a></li>
            <li><a href="/#how-it-works" className="text-sm text-slate-600 hover:text-slate-300 transition-colors">Déploiement</a></li>
            <li><a href="/#faq" className="text-sm text-slate-600 hover:text-slate-300 transition-colors">FAQ</a></li>
          </ul>
        </div>
        <div>
          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Ressources</h4>
          <ul className="space-y-2.5">
            <li><a href="#" className="text-sm text-slate-600 hover:text-slate-300 transition-colors">Documentation</a></li>
            <li><a href="#" className="text-sm text-slate-600 hover:text-slate-300 transition-colors">Guide d'installation</a></li>
            <li><a href="#" className="text-sm text-slate-600 hover:text-slate-300 transition-colors">API Reference</a></li>
            <li><a href="#" className="text-sm text-slate-600 hover:text-slate-300 transition-colors">Changelog</a></li>
            <li><a href="#" className="text-sm text-slate-600 hover:text-slate-300 transition-colors">Support</a></li>
          </ul>
        </div>
        <div>
          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Légal</h4>
          <ul className="space-y-2.5">
            <li><Link to="/cgu" className="text-sm text-slate-600 hover:text-slate-300 transition-colors">CGU</Link></li>
            <li><Link to="/confidentialite" className="text-sm text-slate-600 hover:text-slate-300 transition-colors">Politique de confidentialité</Link></li>
            <li><Link to="/securite-donnees" className="text-sm text-slate-600 hover:text-slate-300 transition-colors">Sécurité des données</Link></li>
            <li><a href="mailto:contact@nafakatech.com" className="text-sm text-slate-600 hover:text-slate-300 transition-colors">Contact</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-800/60 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-xs text-slate-700">&copy; 2026 Nafaka Tech. Tous droits réservés.</p>
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" aria-hidden="true"></span>
          <span className="text-xs text-slate-600">Tous les systèmes opérationnels</span>
        </div>
      </div>
    </div>
  </footer>
);

// Section wave divider
const DemoForm = () => {
  const [form, setForm] = useState({ email: '', company: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch('https://cockpit.nafakatech.com/api/public/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, company: form.company, message: form.message || undefined }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message ? String(data.message) : `Erreur ${res.status}`);
      }
      setStatus('success');
    } catch (err) {
      setErrorMsg(err.message || 'Une erreur est survenue. Réessayez ou écrivez-nous à contact@nafakatech.com');
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="bg-navy-950 py-24" data-testid="contact-section">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <span className="text-xs text-blue-500 dark:text-blue-400 uppercase tracking-widest font-semibold">Contact</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white mt-3 mb-4">
            Voyons ensemble ce que<br/>Cockpit peut faire pour vous.
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-lg max-w-md mx-auto">
            Laissez-nous vos coordonnées, notre équipe vous répond sous 24h.
          </p>
        </div>

        {status === 'success' ? (
          <div className="glass-card rounded-2xl p-10 border border-emerald-500/25 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center mx-auto mb-5">
              <svg className="w-6 h-6 text-emerald-500 dark:text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Demande envoyée !</h3>
            <p className="text-slate-500 dark:text-slate-400">Notre équipe vous contacte sous 24h.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="glass-card rounded-2xl p-8 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Email <span className="text-blue-500 dark:text-blue-400">*</span></label>
                <input
                  type="email" required
                  value={form.email}
                  onChange={e => setForm(f => ({...f, email: e.target.value}))}
                  placeholder="vous@entreprise.com"
                  className="w-full px-4 py-2.5 rounded-lg bg-navy-800 border border-slate-200 dark:border-slate-700/60 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Entreprise <span className="text-blue-500 dark:text-blue-400">*</span></label>
                <input
                  type="text" required
                  value={form.company}
                  onChange={e => setForm(f => ({...f, company: e.target.value}))}
                  placeholder="Nom de votre entreprise"
                  className="w-full px-4 py-2.5 rounded-lg bg-navy-800 border border-slate-200 dark:border-slate-700/60 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Message <span className="text-slate-400 dark:text-slate-500 text-xs font-normal">(optionnel)</span></label>
              <textarea
                value={form.message}
                onChange={e => setForm(f => ({...f, message: e.target.value}))}
                placeholder="Dites-nous en quelques mots votre contexte ou vos questions..."
                rows={4}
                className="w-full px-4 py-2.5 rounded-lg bg-navy-800 border border-slate-200 dark:border-slate-700/60 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:border-blue-500 transition-colors resize-none"
              />
            </div>
            {status === 'error' && (
              <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg px-4 py-2.5">{errorMsg}</p>
            )}
            <button
              type="submit"
              disabled={status === 'loading'}
              className="btn-primary w-full py-3.5 rounded-xl text-white font-bold text-[15px] flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status === 'loading' ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                  Envoi en cours…
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
                  Demander une démo
                </>
              )}
            </button>
            <p className="text-center text-xs text-slate-400 dark:text-slate-500">Réponse sous 24h · Sans engagement</p>
          </form>
        )}
      </div>
    </section>
  );
};

const NAVY = { 900: '#0a0e1a', 800: '#0d1526', 950: '#020817' };
const Wave = ({ from, to, flip }) => (
  <div style={{ background: NAVY[from] }} className="relative overflow-hidden" aria-hidden="true">
    <svg viewBox="0 0 1440 56" fill="none" preserveAspectRatio="none" className="w-full h-10 md:h-14 block" style={flip ? { transform: 'scaleY(-1)' } : undefined}>
      <path d="M0 56L1440 56L1440 28C1200 56 960 0 720 20C480 40 240 4 0 28L0 56Z" fill={NAVY[to]}></path>
    </svg>
  </div>
);

// Scroll to top button
const ScrollToTopButton = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Retour en haut"
      className={`fixed bottom-6 right-6 z-50 w-10 h-10 rounded-full bg-blue-600 hover:bg-blue-500 text-white shadow-lg flex items-center justify-center transition-all duration-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
      </svg>
    </button>
  );
};

// Landing page
function LandingPage() {
  // Initialize reveal animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-navy-900">
      <Navbar />
      <div className="page-enter">
        <HeroSection />
        <TrustedBySection />
        <ProblemSection />
        <ProductPreviewSection />
        <FeaturesSection />
        <AgentSection />
        <HowItWorksSection />
        <SecuritySection />
        {/* <PricingSection /> masqué temporairement */}
        {/* <TestimonialsSection /> masqué temporairement */}
        <FAQSection />
        <FinalCTASection />
        <DemoForm />
        <Footer />
      </div>
      <ScrollToTopButton />
    </div>
  );
}

// ── Legal pages ──────────────────────────────────────────────────────────────

const LegalLayout = ({ title, children }) => {
  const { theme, setTheme } = useTheme();
  return (
    <div className="min-h-screen bg-navy-900">
      <div className="bg-navy-900/95 backdrop-blur-md border-b border-slate-200 dark:border-slate-800/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/"><CockpitLogo /></Link>
            <span className="text-slate-300 dark:text-slate-600 text-sm">/</span>
            <span className="text-sm text-slate-600 dark:text-slate-400">{title}</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-slate-500 hover:text-slate-800 dark:hover:text-white transition-colors">← Retour</Link>
            <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors" aria-label="Basculer le thème">
              {theme === 'dark' ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
              )}
            </button>
          </div>
        </div>
      </div>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-16 pb-24 page-enter">
        <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-2">{title}</h1>
        <p className="text-xs text-slate-500 mb-10">Dernière mise à jour : juillet 2026 · Nafaka Tech SUARL</p>
        <div className="space-y-10">{children}</div>
      </main>
      <Footer />
    </div>
  );
};

const LegalSection = ({ title, children }) => (
  <section>
    <h2 className="text-base font-bold text-slate-900 dark:text-white mb-3 pb-2 border-b border-slate-200 dark:border-slate-700">{title}</h2>
    <div className="space-y-3 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{children}</div>
  </section>
);

const CGUPage = () => (
  <LegalLayout title="Conditions Générales d'Utilisation">
    <LegalSection title="Article 1 — Objet">
      <p>Les présentes Conditions Générales d'Utilisation (CGU) régissent l'accès et l'utilisation de la plateforme Cockpit, éditée par <strong className="text-slate-800 dark:text-slate-200">Nafaka Tech SUARL</strong>, société de droit sénégalais dont le siège social est situé à Dakar, Sénégal.</p>
      <p>Cockpit est une solution SaaS de tableau de bord financier permettant aux entreprises utilisant le logiciel Sage 100 de visualiser et d'analyser leurs données comptables en temps réel, via un agent local installé dans leur infrastructure.</p>
    </LegalSection>

    <LegalSection title="Article 2 — Définitions">
      <ul className="list-disc list-inside space-y-1.5">
        <li><strong className="text-slate-700 dark:text-slate-300">Service :</strong> l'ensemble des fonctionnalités de la plateforme Cockpit accessibles via le site cockpit.nafakatech.com.</li>
        <li><strong className="text-slate-700 dark:text-slate-300">Utilisateur :</strong> toute personne physique accédant au Service pour le compte d'un Abonné.</li>
        <li><strong className="text-slate-700 dark:text-slate-300">Abonné :</strong> toute entreprise ayant souscrit un abonnement au Service.</li>
        <li><strong className="text-slate-700 dark:text-slate-300">Agent :</strong> le logiciel installé sur le réseau local de l'Abonné pour connecter Sage 100 à Cockpit.</li>
        <li><strong className="text-slate-700 dark:text-slate-300">Données Sage :</strong> les données comptables extraites localement de la base de données Sage 100 de l'Abonné.</li>
      </ul>
    </LegalSection>

    <LegalSection title="Article 3 — Accès au Service">
      <p>L'accès au Service est conditionné à la souscription d'un abonnement et à la création d'un compte administrateur. L'Abonné est responsable de la confidentialité de ses identifiants et de toute activité réalisée sous son compte.</p>
      <p>Nafaka Tech se réserve le droit de suspendre ou de résilier l'accès de tout Utilisateur en cas de violation des présentes CGU.</p>
    </LegalSection>

    <LegalSection title="Article 4 — Utilisation du Service">
      <p>L'Abonné s'engage à utiliser le Service conformément à sa destination et aux lois en vigueur. Il est notamment interdit de :</p>
      <ul className="list-disc list-inside space-y-1.5">
        <li>Tenter d'accéder à des données d'autres abonnés.</li>
        <li>Utiliser le Service à des fins frauduleuses ou illicites.</li>
        <li>Reproduire, modifier ou redistribuer le Service ou ses composants.</li>
        <li>Procéder à des tests d'intrusion sans autorisation écrite préalable de Nafaka Tech.</li>
      </ul>
    </LegalSection>

    <LegalSection title="Article 5 — Données de l'Abonné">
      <p>Les Données Sage traitées via l'Agent restent <strong className="text-slate-700 dark:text-slate-300">la propriété exclusive de l'Abonné</strong>. Nafaka Tech agit en qualité de sous-traitant au sens de la réglementation applicable sur la protection des données personnelles.</p>
      <p>L'Agent opère en lecture seule sur la base Sage 100 de l'Abonné. Aucune écriture, modification ou suppression n'est possible sur les données source.</p>
    </LegalSection>

    <LegalSection title="Article 6 — Propriété intellectuelle">
      <p>Le Service, son code source, ses interfaces, ses algorithmes et l'Agent sont la propriété exclusive de Nafaka Tech, protégés par le droit de la propriété intellectuelle. Toute reproduction ou exploitation non autorisée est strictement interdite.</p>
    </LegalSection>

    <LegalSection title="Article 7 — Limitation de responsabilité">
      <p>Nafaka Tech s'engage à maintenir le Service disponible 99,5 % du temps, hors maintenance planifiée. En cas d'indisponibilité, la responsabilité de Nafaka Tech est limitée au montant des frais d'abonnement du mois en cours.</p>
      <p>Nafaka Tech ne saurait être tenue responsable des pertes de données résultant d'une panne de l'infrastructure de l'Abonné ou d'un usage non conforme des présentes CGU.</p>
    </LegalSection>

    <LegalSection title="Article 8 — Résiliation">
      <p>L'Abonné peut résilier son abonnement à tout moment avec un préavis de 30 jours. À l'issue de la période d'abonnement, les données de l'Abonné sont conservées 30 jours supplémentaires avant suppression définitive, sauf demande contraire.</p>
    </LegalSection>

    <LegalSection title="Article 9 — Loi applicable et juridiction">
      <p>Les présentes CGU sont soumises au droit sénégalais. Tout litige relatif à leur interprétation ou exécution sera soumis aux tribunaux compétents de Dakar, Sénégal, après tentative de résolution amiable.</p>
      <p>Pour toute question : <a href="mailto:contact@nafakatech.com" className="text-blue-600 dark:text-blue-400 hover:underline">contact@nafakatech.com</a></p>
    </LegalSection>
  </LegalLayout>
);

const PrivacyPage = () => (
  <LegalLayout title="Politique de confidentialité">
    <LegalSection title="1. Responsable du traitement">
      <p><strong className="text-slate-700 dark:text-slate-300">Nafaka Tech SUARL</strong>, Dakar, Sénégal — <a href="mailto:contact@nafakatech.com" className="text-blue-600 dark:text-blue-400 hover:underline">contact@nafakatech.com</a></p>
    </LegalSection>

    <LegalSection title="2. Données collectées">
      <p><strong className="text-slate-700 dark:text-slate-300">Données de compte :</strong> nom, adresse email professionnelle, nom de l'entreprise, rôle dans l'organisation.</p>
      <p><strong className="text-slate-700 dark:text-slate-300">Données d'utilisation :</strong> logs de connexion, questions posées à Zuri (anonymisées), widgets créés, exports réalisés. Ces données servent à améliorer le Service.</p>
      <p><strong className="text-slate-700 dark:text-slate-300">Données Sage 100 :</strong> traitées localement par l'Agent sur l'infrastructure de l'Abonné. Seuls des agrégats et indicateurs calculés transitent vers nos serveurs — jamais les données comptables brutes.</p>
    </LegalSection>

    <LegalSection title="3. Finalités du traitement">
      <ul className="list-disc list-inside space-y-1.5">
        <li>Fourniture et amélioration du Service.</li>
        <li>Gestion des comptes et de la facturation.</li>
        <li>Support technique et relation client.</li>
        <li>Sécurité et prévention des fraudes.</li>
        <li>Respect des obligations légales.</li>
      </ul>
    </LegalSection>

    <LegalSection title="4. Base légale">
      <p>Le traitement de vos données repose sur : l'exécution du contrat d'abonnement (données de compte et de service), notre intérêt légitime (sécurité, amélioration du Service) et le respect de nos obligations légales.</p>
    </LegalSection>

    <LegalSection title="5. Durée de conservation">
      <p>Les données de compte sont conservées pendant toute la durée de l'abonnement, puis 30 jours après résiliation pour permettre une éventuelle réactivation, avant suppression définitive.</p>
      <p>Les logs d'audit sont conservés 12 mois.</p>
    </LegalSection>

    <LegalSection title="6. Destinataires des données">
      <p>Vos données ne sont ni vendues ni louées à des tiers. Elles peuvent être partagées avec :</p>
      <ul className="list-disc list-inside space-y-1.5">
        <li>Nos sous-traitants techniques (hébergement, monitoring) liés par des engagements de confidentialité stricts.</li>
        <li>Les autorités compétentes, sur réquisition légale.</li>
      </ul>
    </LegalSection>

    <LegalSection title="7. Vos droits">
      <p>Conformément à la réglementation applicable, vous disposez des droits suivants sur vos données personnelles : accès, rectification, effacement, limitation du traitement, portabilité et opposition.</p>
      <p>Pour exercer ces droits : <a href="mailto:contact@nafakatech.com" className="text-blue-600 dark:text-blue-400 hover:underline">contact@nafakatech.com</a>. Réponse sous 30 jours.</p>
    </LegalSection>

    <LegalSection title="8. Sécurité">
      <p>Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données : chiffrement TLS en transit, chiffrement au repos, contrôle d'accès par rôle, audit logs, tests de sécurité réguliers.</p>
      <p>Consultez notre <Link to="/securite-donnees" className="text-blue-600 dark:text-blue-400 hover:underline">page Sécurité des données</Link> pour le détail technique.</p>
    </LegalSection>

    <LegalSection title="9. Cookies">
      <p>Le site cockpitbynafaka.com utilise uniquement des cookies fonctionnels essentiels (session, préférences de thème). Aucun cookie publicitaire ou de tracking tiers n'est utilisé.</p>
    </LegalSection>
  </LegalLayout>
);

const SecurityDataPage = () => (
  <LegalLayout title="Sécurité des données">
    <p className="text-sm text-slate-600 dark:text-slate-400 -mt-6 mb-2">Architecture conçue pour que vos données comptables ne quittent jamais votre infrastructure.</p>

    <LegalSection title="Architecture générale">
      <p>Cockpit repose sur une architecture hybride :</p>
      <ul className="list-disc list-inside space-y-1.5">
        <li><strong className="text-slate-700 dark:text-slate-300">Agent On-Premise :</strong> installé sur votre réseau local, il accède à Sage 100 en lecture seule et calcule les KPIs localement. Les données brutes ne quittent jamais votre infrastructure.</li>
        <li><strong className="text-slate-700 dark:text-slate-300">Cloud Cockpit :</strong> reçoit uniquement les agrégats et indicateurs calculés (CA total, DSO, marge…), jamais les lignes comptables individuelles.</li>
      </ul>
    </LegalSection>

    <LegalSection title="Données en transit">
      <p>Toutes les communications entre l'Agent et nos serveurs cloud sont chiffrées en <strong className="text-slate-700 dark:text-slate-300">TLS 1.3</strong>. L'Agent initie les connexions sortantes — aucune connexion entrante n'est nécessaire depuis l'extérieur, ce qui élimine l'exposition d'un port ouvert dans votre pare-feu.</p>
    </LegalSection>

    <LegalSection title="Données au repos">
      <p>Les agrégats stockés dans notre infrastructure cloud sont chiffrés avec <strong className="text-slate-700 dark:text-slate-300">AES-256</strong>. Chaque abonné dispose d'un espace de données logiquement isolé.</p>
    </LegalSection>

    <LegalSection title="Accès à Sage 100 — lecture seule">
      <p>L'Agent se connecte à votre base Sage 100 avec un compte dédié configuré en <strong className="text-slate-700 dark:text-slate-300">lecture seule</strong>. Il est techniquement impossible pour Cockpit d'écrire, modifier ou supprimer des données dans votre base comptable.</p>
      <p>Vos identifiants Sage sont chiffrés et stockés localement selon les standards de sécurité Windows (DPAPI), liés à votre poste. Nafaka Tech n'y a jamais accès.</p>
    </LegalSection>

    <LegalSection title="Contrôle d'accès">
      <p>Cockpit implémente un contrôle d'accès basé sur les rôles (RBAC) avec 5 niveaux de permission prédéfinis. L'authentification utilise des tokens JWT à durée de vie courte avec rotation automatique. L'authentification multi-facteurs (MFA) est disponible pour tous les comptes.</p>
    </LegalSection>

    <LegalSection title="Audit log complet">
      <p>Chaque action est enregistrée : connexions, questions posées, exports, modifications de configuration. Les logs sont immuables, conservés 12 mois et exportables pour vos audits internes. Les données personnelles des utilisateurs (emails, noms) sont partiellement masquées dans les logs.</p>
    </LegalSection>

    <LegalSection title="Gestion des incidents">
      <p>En cas d'incident de sécurité affectant vos données, Nafaka Tech s'engage à :</p>
      <ul className="list-disc list-inside space-y-1.5">
        <li>Vous notifier dans un délai de <strong className="text-slate-700 dark:text-slate-300">72 heures</strong> après détection.</li>
        <li>Fournir une analyse de cause racine sous 7 jours.</li>
        <li>Mettre en œuvre les mesures correctives nécessaires.</li>
      </ul>
    </LegalSection>

    <LegalSection title="Tests de sécurité">
      <p>Nafaka Tech réalise des audits de sécurité réguliers et des tests de pénétration annuels sur son infrastructure cloud. L'Agent fait l'objet d'analyses de code statique à chaque version.</p>
      <p>Pour signaler une vulnérabilité : <a href="mailto:security@nafakatech.com" className="text-blue-600 dark:text-blue-400 hover:underline">security@nafakatech.com</a></p>
    </LegalSection>
  </LegalLayout>
);

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/cgu" element={<CGUPage />} />
        <Route path="/confidentialite" element={<PrivacyPage />} />
        <Route path="/securite-donnees" element={<SecurityDataPage />} />
      </Routes>
    </BrowserRouter>
  );
}
